import { useUserScopedQueryKey, QueryContext } from "@/hooks/useUserScopedQueryKey";

/**
 * Hook to create optimized query keys that prevent cache invalidation bugs
 * Provides consistent key generation to avoid inbox staleness issues
 */
export function useOptimizedQueryKey(contextOverride?: QueryContext) {
  const { createQueryKey, asQueryKeyTuple, organizationId, userId, effectiveContext } = 
    useUserScopedQueryKey(contextOverride);

  const createOrgQueryKey = (
    baseKey: string | readonly string[],
    ...additionalKeys: readonly (string | number | undefined)[]
  ): readonly string[] => {
    if (!organizationId) {
      console.warn("Creating org query key without organization context");
    }

    const normalizedBase = Array.isArray(baseKey) ? [...baseKey] : baseKey;
    const extras = [...additionalKeys];

    // Use tuple-aware generator that filters undefined values
    return asQueryKeyTuple(normalizedBase as string | string[], ...extras);
  };

  const createUserQueryKey = (
    baseKey: string | readonly string[],
    ...additionalKeys: readonly (string | number | undefined)[]
  ): readonly string[] => {
    if (!userId) {
      console.warn("Creating user query key without user context");
    }

    const normalizedBase = Array.isArray(baseKey) ? [...baseKey] : baseKey;
    const extras = [...additionalKeys];

    // Use tuple-aware generator that filters undefined values
    return asQueryKeyTuple(normalizedBase as string | string[], ...extras);
  };

  const createInboxQueryKey = (): readonly string[] => {
    // Consistent inbox key prevents stale cache after updates
    return organizationId 
      ? createOrgQueryKey('inbox')
      : createUserQueryKey('inbox');
  };

  const createApplicationsQueryKey = (): readonly string[] => {
    return organizationId 
      ? createOrgQueryKey('applications')
      : createUserQueryKey('applications');
  };

  /**
   * Creates organization-aware query guard helper
   * Prevents queries from running when organization context is missing
   */
  const createOrgQueryGuard = () => ({
    enabled: !!organizationId,
    organizationId,
  });

  return {
    createQueryKey,
    asQueryKeyTuple,
    createOrgQueryKey,
    createUserQueryKey,
    createInboxQueryKey,
    createApplicationsQueryKey,
    createOrgQueryGuard,
    organizationId,
    userId,
    effectiveContext
  };
}